import { filterProjects, projectDate } from '@/lib/projectFilters';
import { CharacterPicker } from '@/components/CharacterPicker';
import {
  ASPECT_KEYS,
  ASPECTS,
  type Aspect,
  LANGUAGES,
  type ProjectDto,
  type ProjectSummaryDto,
  SIZE_KEYS,
  SIZES,
  STYLE_PRESETS,
  type VideoSize,
} from '@studio/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Clapperboard, Copy, Film, Folder, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog, NativeSelect } from '@/components/ui/controls';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/form';
import { EmptyState, ErrorAlert, PageHeader, Skeleton } from '@/components/ui/misc';
import { api, errorMessage } from '@/lib/api';
import { useProducts } from '@/lib/queries';
import { ago } from '@/lib/utils';

type FolderDto = { id: string; title: string };
type FolderProject = ProjectSummaryDto & { folderId: string; createdAt?: string };
function NewProjectDialog({ open, onOpenChange, folderId }: { open: boolean; onOpenChange: (v: boolean) => void; folderId: string }) {
  const nav = useNavigate();
  const { data: products } = useProducts();
  const [title, setTitle] = useState('');
  const [productId, setProductId] = useState('');
  const [language, setLanguage] = useState('Hindi');
  const [style, setStyle] = useState<string>(STYLE_PRESETS[0]);
  const [aspect, setAspect] = useState<Aspect>('9:16');
  const [size, setSize] = useState<VideoSize>('hd');
  const [castIds, setCastIds] = useState<string[]>([]);
  const create = useMutation({
    mutationFn: () =>
      api<ProjectDto>('/api/projects', {
        method: 'POST',
        body: { folderId, title: title.trim(), productId: productId || null, language, style, aspect, size, castIds },
      }),
    onSuccess: (p) => {
      onOpenChange(false);
      nav(`/projects/${p.id}?tab=script`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>New video</DialogTitle>
          <DialogDescription>One project makes one ad. Everything can be changed later.</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 overflow-y-auto overscroll-contain grid gap-4 sm:grid-cols-2 pr-2">
          <Field label="Title" className="sm:col-span-2">
            {(p) => (
              <Input
                {...p}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Hair Booster Oil — Diwali ad"
              />
            )}
          </Field>
          <Field label="Product">
            {(p) => (
              <NativeSelect {...p} value={productId} onChange={(e) => setProductId(e.target.value)}>
                <option value="">— none —</option>
                {products?.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.title}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label="Language of the dialogue">
            {(p) => (
              <NativeSelect {...p} value={language} onChange={(e) => setLanguage(e.target.value)}>
                {LANGUAGES.map((l) => (
                  <option key={l}>{l}</option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label="Style">
            {(p) => (
              <NativeSelect {...p} value={style} onChange={(e) => setStyle(e.target.value)}>
                {STYLE_PRESETS.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label="Shape">
            {(p) => (
              <NativeSelect {...p} value={aspect} onChange={(e) => setAspect(e.target.value as Aspect)}>
                {ASPECT_KEYS.map((a) => (
                  <option key={a} value={a}>
                    {ASPECTS[a].label}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label="Size" hint={SIZES[size].hint}>
            {(p) => (
              <NativeSelect {...p} value={size} onChange={(e) => setSize(e.target.value as VideoSize)}>
                {SIZE_KEYS.map((k) => (
                  <option key={k} value={k}>
                    {SIZES[k].label}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <div className="sm:col-span-2">
            <div className="mb-1.5 text-sm font-medium">Characters</div>
            <CharacterPicker value={castIds} onChange={setCastIds} />
          </div>
        </div>
        <DialogFooter className="shrink-0">
          <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!title.trim()}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ProjectsPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const q = useQuery({ queryKey: ['projects'], queryFn: () => api<FolderProject[]>('/api/projects') });
  const [params, setParams] = useSearchParams();
  const folderId = params.get('folder');
  const folders = useQuery({ queryKey: ['folders'], queryFn: () => api<FolderDto[]>('/api/folders') });
  const folder = folders.data?.find((f) => f.id === folderId);
  const today = projectDate(new Date());
  const search = params.get('search') ?? '';
  const from = params.get('from') ?? today;
  const to = params.get('to') ?? today;
  const invalidRange = !!from && !!to && from > to;
  const setFilters = (changes: Record<string, string>) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(changes)) next.set(key, value);
      return next;
    }, { replace: true });
  };
  const folderVideos = q.data?.filter((p) => p.folderId === folder?.id);
  const videos = folderVideos ? filterProjects(folderVideos, search, from, to) : undefined;
  const [addingFolder, setAddingFolder] = useState(false);
  const [folderName, setFolderName] = useState('');
  const addFolder = useMutation({
    mutationFn: () => api<FolderDto>('/api/folders', { method: 'POST', body: { title: folderName.trim() } }),
    onSuccess: async (f) => {
      await qc.invalidateQueries({ queryKey: ['folders'] });
      setAddingFolder(false);
      setFolderName('');
      setParams({ folder: f.id });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<ProjectSummaryDto | null>(null);
  const dup = useMutation({
    mutationFn: (p: ProjectSummaryDto) =>
      api<ProjectDto>(`/api/projects/${p.id}/duplicate`, { method: 'POST', body: { title: `${p.title} (copy)` } }),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      nav(`/projects/${p.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const del = useMutation({
    mutationFn: (p: ProjectSummaryDto) => api(`/api/projects/${p.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleting(null);
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title={folder ? folder.title : "Projects"}
        description={folder ? "Create and manage videos in this folder." : "Create a folder, then add your videos inside it."}
        actions={
          <Button onClick={() => folder ? setCreating(true) : setAddingFolder(true)}>
            <Plus /> {folder ? "New video" : "New folder"}
          </Button>
        }
      />
      {folder && <Button variant="outline" onClick={() => setParams({})}><ArrowLeft /> All folders</Button>}
      {folder && <Card className="space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="font-medium">{from === today && to === today ? "Today's projects" : !from && !to ? "All projects" : "Projects by date"}</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setFilters({ from: projectDate(new Date()), to: projectDate(new Date()) })}>Today</Button>
            <Button variant="outline" size="sm" onClick={() => setFilters({ from: '', to: '' })}>All dates</Button>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr]">
          <Field label="Search projects">{(p) => <Input {...p} type="search" placeholder="Search by project name…" value={search} onChange={(e) => setFilters({ search: e.target.value })} />}</Field>
          <Field label="From">{(p) => <Input {...p} type="date" value={from} max={to || undefined} onChange={(e) => setFilters({ from: e.target.value })} />}</Field>
          <Field label="To">{(p) => <Input {...p} type="date" value={to} min={from || undefined} onChange={(e) => setFilters({ to: e.target.value })} />}</Field>
        </div>
        <p className="text-xs text-muted-foreground">Created date · India time (IST) · {videos?.length ?? 0} of {folderVideos?.length ?? 0} projects</p>
        {invalidRange && <p role="alert" className="text-sm text-destructive">From date must be on or before To date.</p>}
      </Card>}
      {folders.error && <ErrorAlert message={errorMessage(folders.error)} />}
      {!folder && <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {folders.data?.map((f) => <Card key={f.id}>
          <button className="flex w-full items-center gap-4 p-5 text-left hover:bg-accent/40" onClick={() => setParams({ folder: f.id })}>
            <Folder className="size-10 shrink-0 text-primary" />
            <div className="min-w-0"><div className="font-medium break-words">{f.title}</div>
              <div className="text-sm text-muted-foreground">{q.data?.filter((p) => p.folderId === f.id).length ?? 0} videos</div>
            </div>
          </button>
        </Card>)}
      </div>}
      {q.error && <ErrorAlert message={errorMessage(q.error)} />}
      {q.isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      )}
      {folder && videos && !videos.length && !invalidRange && (
        <EmptyState
          icon={<Clapperboard />}
          title={folderVideos?.length ? "No matching projects" : "No videos in this folder"}
          description={folderVideos?.length ? "Try another date range or search. Choose All dates to include older projects." : "Make your first video ad."}
          action={<Button onClick={() => setCreating(true)}>New video</Button>}
        />
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {folder && videos?.map((p) => (
          <Card key={p.id} className="flex flex-col overflow-hidden">
            <Link to={`/projects/${p.id}`} className="flex flex-1 gap-3 p-3 hover:bg-accent/40">
              <div className="grid h-36 w-20 shrink-0 place-items-center overflow-hidden rounded-md bg-muted">
                {p.thumbnail ? (
                  <img src={p.thumbnail.url} alt="" className="size-full object-cover" />
                ) : (
                  <Film className="size-6 text-muted-foreground" />
                )}
              </div>
              <div className="min-w-0 space-y-1.5">
                <div className="font-medium leading-snug">{p.title}</div>
                <div className="flex flex-wrap gap-1">
                  {p.isDemo && <Badge>demo</Badge>}
                  <Badge variant="secondary">{p.aspect}</Badge>
                  <Badge variant="secondary">{p.language}</Badge>
                  {p.finalVideo && <Badge variant="success">finished video</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">{p.product?.title ?? 'No product'}</div>
                <div className="text-xs text-muted-foreground">
                  {p.shotsDone}/{p.shots} shots made · {ago(p.updatedAt)}
                </div>
              </div>
            </Link>
            <div className="flex gap-1 border-t px-2 py-1.5">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => dup.mutate(p)}
                loading={dup.isPending && dup.variables?.id === p.id}
              >
                <Copy /> Duplicate
              </Button>
              {!p.isDemo && (
                <Button size="sm" variant="ghost" className="ml-auto text-destructive" onClick={() => setDeleting(p)}>
                  <Trash2 /> Delete
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>
      {folder && <NewProjectDialog key={folder.id} open={creating} onOpenChange={setCreating} folderId={folder.id} />}
      <Dialog open={addingFolder} onOpenChange={setAddingFolder}>
        <DialogContent>
          <DialogHeader><DialogTitle>New folder</DialogTitle><DialogDescription>Group related videos in one folder.</DialogDescription></DialogHeader>
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if(folderName.trim() && !addFolder.isPending) addFolder.mutate(); }}>
            <Field label="Folder name">{(p) => <Input {...p} autoFocus maxLength={100} placeholder="e.g. Product ads" value={folderName} onChange={(e) => setFolderName(e.target.value)} />}</Field>
            <DialogFooter><Button type="submit" disabled={!folderName.trim()} loading={addFolder.isPending}>Create folder</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        title={`Delete "${deleting?.title}"?`}
        description="Its shots and takes are deleted too. This cannot be undone."
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={() => deleting && del.mutate(deleting)}
      />
    </div>
  );
}
